import React, { useContext } from 'react';
import { FormikProps } from 'formik';
import { CFormGroup, CImg, CInput, CLabel, CTooltip } from '@coreui/react';
import { get } from 'lodash';
import classNames from 'classnames';
import PhoneInput from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { UserPasswordPayload, UserPayload, UserRoles } from '../../shared/models/user';
import Select from '../../shared/components/form/select/Select';
import ImageInput from '../../shared/components/form/ImageInput/ImageInput';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import { selectUserProfile } from '../../store/selectors/profile.selectors';
import COUNTRIES from '../../shared/constants/phone-countries.constants';
import tooltip from '../../assets/images/tool_tip.svg';
import { selectOrgList } from '../../store/selectors/orgHierarchy.selectors';

export enum TechnicianFormMode {
  Add,
  Edit,
  Profile,
}

const AdminRoleOption = {
  value: UserRoles.Admin,
  label: 'Admin',
};
const ManagerRoleOption = {
  value: UserRoles.Manager,
  label: 'Manager',
};
const AgentRoleOption = {
  value: UserRoles.Agent,
  label: 'Agent',
};

const roleOptions = (profileRoles: Array<UserRoles>, userRoles: Array<UserRoles>) => {
  const options: any = [
    AgentRoleOption
  ];

  if (userRoles.indexOf(UserRoles.Admin) > -1) {
    options.push({
      ...AdminRoleOption,
      isFixed: true
    });
  }

  if (profileRoles.indexOf(UserRoles.Manager) > -1 || profileRoles.indexOf(UserRoles.Admin) > -1) {
    options.push(ManagerRoleOption);
  } else if (userRoles.indexOf(UserRoles.Manager) > -1) {
    options.push({
      ...ManagerRoleOption,
      isFixed: true
    });
  }
  return options;
};

export interface TechnicianFormProps {
  formik: FormikProps<UserPayload & UserPasswordPayload>;
  disabled?: boolean;
  mode: TechnicianFormMode;
}

const TechnicianForm = ({ formik, disabled, mode}: TechnicianFormProps) => {
  const labels = useContext(ConfigContext);
  const {
    enableOrgHierarchyModule,
  } = useFlags();
  const isInvalid = (fieldName: string) => get(formik.touched, fieldName) && get(formik.errors, fieldName);
  const profileRoles = useSelector(selectUserProfile)?.roles;
  const orgs = useSelector(selectOrgList);

  return (
    <>
      <ImageInput
        name="avatar"
        disabled={disabled}
      />
      <CFormGroup>
        <CLabel htmlFor="name">Name</CLabel>
        <CTooltip placement="top" content={`Name of  ${labels.agent.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
        </CTooltip>
        <CInput
          id="name"
          type="text"
          placeholder={`${labels.agent} Name`}
          name="name"
          className={classNames({'is-invalid': isInvalid('name')})}
          disabled={disabled}
          { ...formik.getFieldProps('name') }
        />
        {isInvalid('name') && <div className="invalid-feedback">{ formik.errors.name }</div>}
      </CFormGroup>

      <CFormGroup>
        <CLabel htmlFor="phone">Phone</CLabel>
        <CTooltip placement="top" content={`Phone number of ${labels.agent.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
        </CTooltip>
        {/* @ts-ignore */}
        <PhoneInput
          id="phone"
          type="text"
          placeholder="Phone Number"
          name="phone"
          value={ formik.values.phone }
          countries={COUNTRIES}
          disabled={disabled}
          addInternationalOption={false}
          onChange={ (value) => formik.setFieldValue('phone', value) }
          onBlur={formik.getFieldProps('phone').onBlur}
          className={classNames('form-control', {'is-invalid': isInvalid('phone')})}
          defaultCountry="US"
        />
        {isInvalid('phone') && <div className="invalid-feedback">{ formik.errors.phone }</div>}
      </CFormGroup>

      {
        (mode === TechnicianFormMode.Edit || mode === TechnicianFormMode.Profile) && (
          <CFormGroup>
            <CLabel htmlFor="username">
              Username
            </CLabel>
            <CTooltip placement="top" content={`${labels.agent} username`}>
              <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
            </CTooltip>
            <CInput
              id="username"
              type="text"
              placeholder="Username"
              name="username"
              disabled
              { ...formik.getFieldProps('username') }
            />
          </CFormGroup>
        )
      }

      <CFormGroup>
        <CLabel htmlFor="email">
          Email
          <CTooltip placement="top" content={mode === TechnicianFormMode.Add ? 'An Email address will be used as the username' : 'Updating the user’s email does not update the username'}>
            <CImg src={tooltip} className="glympser-toolTip ml-1 mb-2"/>
          </CTooltip>
        </CLabel>
        <CInput
          id="email"
          type="email"
          placeholder="Email"
          name="email"
          className={classNames({'is-invalid': isInvalid('email')})}
          disabled={disabled}
          { ...formik.getFieldProps('email') }
        />
        {isInvalid('email') && <div className="invalid-feedback">{ formik.errors.email }</div>}
      </CFormGroup>

      <CFormGroup>
        <CLabel htmlFor="roles">Roles</CLabel>
        <Select
          isMulti
          name="roles"
          id="roles"
          options={roleOptions(profileRoles, formik.values.roles)}
          className={classNames({ 'is-invalid': isInvalid('roles') })}
          classNamePrefix="select"
          isDisabled={disabled}
          {...formik.getFieldProps('roles')}
          onBlur={() => formik.setFieldTouched('roles', true, true)}
          onChange={(values) => formik.setFieldValue('roles', values)}
        />
        {isInvalid('roles') && <div className="invalid-feedback">{formik.errors.roles}</div>}
      </CFormGroup>

      {
        (mode === TechnicianFormMode.Edit && enableOrgHierarchyModule) ? (
          <CFormGroup>
            <CLabel htmlFor="roles">Organization</CLabel>
            <Select
              isMulti={false}
              name="organization"
              id="organization"
              options={orgs?.map((org) => {
                return { value: org?._id, label: org?.name};
              })}
              className={classNames({ 'is-invalid': isInvalid('organization') })}
              classNamePrefix="select"
              isDisabled={disabled}
              {...formik.getFieldProps('organization')}
              onBlur={() => formik.setFieldTouched('organization', true, true)}
              onChange={(values) => formik.setFieldValue('organization', values)}
            />
            {isInvalid('organization') && <div className="invalid-feedback">{formik.errors.organization}</div>}
          </CFormGroup>
        ) : null
      }
    </>
  );
};

TechnicianForm.defaultProps = {
  disabled: false
};

export default TechnicianForm;
